import { useCallback } from 'react';
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { Database } from 'libs/end-to-end/src/types/databases/database';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { DataSets } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';
import React from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { map } from 'lodash';
import { mapDataSetDependentsToDataSet } from 'libs/end-to-end/src/map-data-set-dependents-to-data-set';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';

export interface DatabasesDataSetsPageHook {
  databaseName: string;
  activeRows: AdminTableRow[];
  archivedRows: AdminTableRow[];
  headings: string[];
  newDataSetButtonLabel: string;
  newDataSetButtonBehavior: TouchTargetBehavior;
  continueToTransformButtonLabel: string;
  continueToTransformButtonBehavior: TouchTargetBehavior;
  continueToTransformButtonDisabled: boolean;
  backButtonLabel: string;
  backButtonBehavior: TouchTargetBehavior;
  backButtonDisabled: boolean;
  organizationId: string;
  databaseId: string;
}

export const useDatabasesDataSetsPage = (): DatabasesDataSetsPageHook => {
  const { organizationId, databaseId } = useParams();
  const navigate = useNavigateSafe();

  if (!organizationId || !databaseId) {
    throw new Error('organizationId and databaseId are required');
  }

  const readDatabaseGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}`;
  }, [organizationId, databaseId]);

  const readDatabasePattern = useSimpleReadPattern({
    getUrlFn: readDatabaseGetUrlFn,
    responseSchema: Database,
    canRead: true,
  });

  const patternGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const pattern = useSimpleReadPattern({
    responseSchema: DataSets,
    canRead: true,
    getUrlFn: patternGetUrlFn,
  });

  return useMemo(() => {
    const dataSets = pattern.status === 'done' ? pattern.entity.dataSets : [];
    const dataSetVersions =
      pattern.status === 'done' ? pattern.entity.dataSetVersions : {};

    const dependentDataSetsMap = mapDataSetDependentsToDataSet(dataSets);
    const rows: {
      dataSet: DataSet;
      rowContents: AdminTableRow;
    }[] = dataSets.map((dataSet) => {
      const versionIdsForDataSetId = dataSetVersions[dataSet.id] ?? [];

      const dependentDataSets = dependentDataSetsMap[dataSet.id] || [];

      return {
        dataSet: dataSet,
        rowContents: {
          key: dataSet.id,
          columns: [
            {
              key: 'name',
              columnType: 'custom',
              ui: (
                <Spacing size={'sm'} direction={'vertical'}>
                  <S2Typography
                    variant={'headline-6-alt'}
                    translatedContent={dataSet.displayName}
                  />

                  <S2Typography
                    variant={'headline-7-alt'}
                    translatedContent={convertDataSetTypeToString(dataSet)}
                  />
                </Spacing>
              ),
            },
            {
              key: 'edit',
              columnType: 'button',
              buttonText: 'Edit',
              onClick: () => {
                navigate(
                  `/organizations/${organizationId}/databases/${databaseId}/data-sets/edit/${dataSet.id}`,
                );
              },
            },
            {
              key: 'dependent_data_sets',
              columnType: 'custom',
              ui: (
                <Spacing size={'sm'} direction={'vertical'}>
                  {dependentDataSets.length > 0 &&
                    dependentDataSets.map((dependentDataSet) => (
                      <TouchTarget
                        key={dependentDataSet.id}
                        content={() => {
                          return (
                            <S2Typography
                              variant={'link'}
                              key={dependentDataSet.id}
                              translatedContent={dependentDataSet.name}
                            />
                          );
                        }}
                        behavior={{
                          type: 'onClick',
                          onClick: () => {
                            navigate(
                              `/organizations/${organizationId}/databases/${databaseId}/data-sets/edit/${dependentDataSet.id}`,
                            );
                          },
                        }}
                      />
                    ))}
                  {dependentDataSets.length === 0 && (
                    <S2Typography
                      variant={'body-6'}
                      translatedContent={'No dependent data sets'}
                    />
                  )}
                </Spacing>
              ),
            },
            {
              key: 'versionHistory',
              columnType: 'text',
              text: `${versionIdsForDataSetId.length} version(s)`,
            },
            {
              key: 'pullUpload',
              columnType: 'button',
              buttonText: 'Pull / Upload',
              onClick: () => {
                navigate(
                  `/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSet.id}/pull-upload`,
                );
              },
            },
            {
              key: 'postProcessingScript',
              columnType: 'button',
              buttonText: 'Post Processing Script',
              onClick: () => {
                navigate(
                  `/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSet.id}/post-processing-script`,
                );
              },
            },
          ],
        },
      };
    });
    const activeRows = rows
      .filter((row) => !row.dataSet.isArchived)
      .map((row) => row.rowContents);
    const archivedRows = rows
      .filter((row) => row.dataSet.isArchived)
      .map((row) => row.rowContents);
    return {
      organizationId,
      databaseId,
      databaseName:
        readDatabasePattern.status === 'done'
          ? readDatabasePattern.entity.name
          : '',
      newDataSetButtonBehavior: {
        type: 'onClick',
        onClick: () =>
          navigate(
            `/organizations/${organizationId}/databases/${databaseId}/data-sets/new`,
          ),
      },
      newDataSetButtonLabel: 'New Data Set',
      headings: [
        'Name',
        'Edit',
        'Dependent Data Sets',
        'Versions',
        'Pull / Upload',
        'Post Processing Script',
      ],
      continueToTransformButtonLabel: 'Continue to Transform',
      continueToTransformButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          navigate(
            `/organizations/${organizationId}/databases/${databaseId}/transform`,
          );
        },
      },
      continueToTransformButtonDisabled:
        pattern.status !== 'done' || pattern.entity.dataSets.length === 0,
      backButtonDisabled: pattern.status !== 'done',
      backButtonLabel: 'Back',
      backButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          navigate(
            `/organizations/${organizationId}/databases/edit/${databaseId}`,
          );
        },
      },
      activeRows: activeRows,
      archivedRows: archivedRows,
    };
  }, [databaseId, navigate, organizationId, readDatabasePattern, pattern]);
};

const convertDataSetTypeToString = (dataSet: DataSet): string => {
  switch (dataSet.config.dataSetType) {
    case 'File':
      return 'Flat File';
    case 'Supplemental':
      if (dataSet.config.supplementalDataSetType === 'resumes') {
        return 'Supplemental - Resumes';
      } else if (
        dataSet.config.supplementalDataSetType === 'external_pay_benchmarking'
      ) {
        return 'Supplemental - External Pay Benchmarking';
      } else if (
        dataSet.config.supplementalDataSetType === 'internal_pay_benchmarking'
      ) {
        return 'Supplemental - Internal Pay Benchmarking';
      } else {
        throw guardExhaustive(dataSet.config.supplementalDataSetType);
      }
    case 'Integration':
      if (dataSet.config.integration.integrationType === 'ADP') {
        return 'ADP Integration';
      } else if (dataSet.config.integration.integrationType === 'Greenhouse') {
        return 'Greenhouse Integration';
      } else if (dataSet.config.integration.integrationType === 'Postgres') {
        return 'Postgres Integration';
      } else if (dataSet.config.integration.integrationType === 'HTTP') {
        return 'HTTP Integration';
      } else if (dataSet.config.integration.integrationType === 'Workday') {
        return 'Workday Integration';
      } else if (dataSet.config.integration.integrationType === 'Merge') {
        return 'Merge Integration';
      } else if (dataSet.config.integration.integrationType === 'ShareFile') {
        return 'ShareFile Integration';
      } else if (dataSet.config.integration.integrationType === 'Snowflake') {
        return 'Snowflake Integration';
      } else if (dataSet.config.integration.integrationType === 'Peregrine') {
        return 'Peregrine Integration';
      } else if (dataSet.config.integration.integrationType === 'SqlServer') {
        return 'SQL Server Integration';
      } else {
        throw guardExhaustive(dataSet.config.integration);
      }
    case 'Composite':
      if (!dataSet.config.recipe) {
        return 'Custom Composite';
      }
      if (dataSet.config.recipe.type === 'merge-resume-parsing') {
        return 'Merge Resume Parsing';
      } else if (dataSet.config.recipe.type === 'rename-columns') {
        return 'Rename Columns';
      } else if (dataSet.config.recipe.type === 'common-model-validation') {
        return 'Common Model Validation';
      } else if (dataSet.config.recipe.type === 'employee-candidate-matching') {
        return 'Employee Candidate Matching';
      } else if (dataSet.config.recipe.type === 'ai-interviewer') {
        return 'AI Interviewer';
      } else if (dataSet.config.recipe.type === 'commute-times') {
        return 'Commute Times';
      } else if (dataSet.config.recipe.type === 'resume-address-parser') {
        return 'Resume Address Parser';
      } else if (dataSet.config.recipe.type === 'merge-filtering') {
        return 'Merge Filtering';
      } else if (dataSet.config.recipe.type === 'resume-fetcher') {
        return 'Resume Fetcher';
      } else if (dataSet.config.recipe.type === 'resume-parser') {
        return 'Resume Parser';
      } else if (dataSet.config.recipe.type === 'dagster-source') {
        return 'Dagster Source';
      } else {
        throw guardExhaustive(dataSet.config.recipe);
      }
  }
};
